// De regelingenstrook (funding fase 5, F5 a + G4 a; ADR
// docs/decisions/2026-10-06-funding-werkend.md, fase 5).
//
// ÉÉN COMPONENT, DRIE PLEKKEN: skill-detail, trend-detail en de kansfiche van
// de Plan-tab (editie 11). Alle drie lezen dezelfde route
// GET /api/funding/voor?sector=&vorm=&bedrijfstype=&limiet=3: de open en
// aangekondigde regelingen voor de sector (sector-eigen én sectorbreed),
// gerangschikt op de pasmaat (bedrag, eenvoud, tijd, passend; 0-10).
//
// RUIM KOPPELEN, KORT TONEN. Binnen de sector past elke open regeling bij
// elke skill — de randvoorwaarden van een regeling zijn wie, wat en wanneer,
// nooit een vaardigheid. Dus: drie regels, de doelgroep-eis als subregel (dat
// is de poort: "alleen aangesloten werkgevers"), de uitleg van de pasmaat op
// hover en focus, en een voet naar "alle N voor <sector> op de Funding-tab".
// Altijd het label "Kansrijk" met zijn uitleg als tooltip (ownerwens 08-10-2026;
// tot dan "afgeleide suggestie, geen beoordeling van jouw situatie" — inhoudelijk
// is het dat nog steeds, zie lib/kansrijk.ts en Kansrijk.tsx).
// Nooit een percentage, nooit een kans, nooit een tekstmatch op een skillnaam.

import { useState, type ReactNode } from "react";

import type { FundingVoorItem, Skill } from "../contracts";
import { useFundingVoor } from "../lib/funding";
import { legeVeldTekst } from "./FundingAanvulling";
import { vondstLabel } from "../lib/fundingVondst";
import { KANSRIJK_LABEL, KANSRIJK_UITLEG } from "../lib/kansrijk";
import { Kansrijk } from "./Kansrijk";
import { naarTab } from "../lib/naarTab";
import { color, radius, size, weight } from "../tokens";

/** Schermlabel → sleutel in config/sectors.yaml; alleen eenduidige vertalingen
 *  (de omkering van service/src/sof/readapi/mappers.py#SECTOR_LABELS, beperkt
 *  tot sleutels die in sectors.yaml staan). Pilot gereed 26-10 (baan 1C): de
 *  pilotsectoren (proposities.yaml sectornaam) horen erbij; de server vertaalt
 *  een pilotsector naar de bedrijfstakken van zijn school (E&O: retail +
 *  zakelijk), dus de strook vraagt de sector en krijgt die regelingen. */
export const SECTOR_SLEUTEL: Partial<Record<Skill["sector"], string>> = {
  ICT: "ict",
  Zorg: "zorg",
  Bouw: "bouw",
  Logistiek: "logistiek",
  Retail: "retail",
  Techniek: "techniek",
  "Economie & Ondernemen": "economie_ondernemen",
  Veiligheid: "veiligheid",
  "Kinderopvang (pilot)": "kinderopvang",
};

/** De filterwaarde van de Funding-tab voor "alleen sectorbreed". */
export const SECTORBREED = "sectorbreed";

/** Het label boven de strook; de ene bron is lib/kansrijk.ts (en de API). */
export const STROOK_LABEL = KANSRIJK_LABEL;

/**
 * De sector van een context met één of meer V1-labels (skill: één, trend: een
 * lijst). Eén eenduidige sleutel → die sleutel; anders sectorbreed. Zo zegt
 * de strook nooit iets over een sector die niet vaststaat.
 */
export function sectorVoorLabels(
  labels: readonly string[] | null | undefined,
): { sleutel: string; eenduidig: boolean } {
  // Geen lijst (een trend uit een oudere bron of een onvolledige mock): sectorbreed.
  if (!Array.isArray(labels)) return { sleutel: SECTORBREED, eenduidig: false };
  const sleutels = new Set<string>();
  for (const l of labels) {
    const s = SECTOR_SLEUTEL[l as Skill["sector"]];
    if (s) sleutels.add(s);
  }
  if (sleutels.size === 1 && labels.length > 0 && labels.every((l) => SECTOR_SLEUTEL[l as Skill["sector"]])) {
    return { sleutel: [...sleutels][0]!, eenduidig: true };
  }
  return { sleutel: SECTORBREED, eenduidig: false };
}

function komma(n: number): string {
  return n.toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function StatusMerk({ item }: { item: FundingVoorItem }) {
  const nu = item.pasmaat.handelNu;
  const open = item.regeling.status === "open";
  // Review fase 6 (harde regel 2): een status uit de export van 25-08 zegt
  // dat hij uit de export komt; de volle statuszin staat in de tooltip.
  const export_ = item.regeling.statusHerkomst === "export";
  const kaal = nu ? "handel nu" : open ? "open" : item.regeling.status;
  const tekst = export_ ? `${kaal} · export` : kaal;
  const fg = nu ? color.accentAmber : open ? color.accentGreen : color.soft;
  const bg = nu ? color.accentAmberBg : open ? "#ecfdf5" : color.surfaceAlt;
  return (
    <span
      title={item.regeling.statusTekst ?? (export_ ? "uit de export van 25-08-2026" : undefined)}
      style={{
        fontSize: 10.5,
        fontWeight: weight.medium,
        color: fg,
        background: bg,
        border: `1px solid ${color.border}`,
        borderRadius: radius.pill,
        padding: "1px 7px",
        whiteSpace: "nowrap",
      }}
    >
      {tekst}
    </span>
  );
}

function Rij({
  item,
  laatste,
  onOpen,
  labelUitleg,
}: {
  item: FundingVoorItem;
  laatste: boolean;
  onOpen: (item: FundingVoorItem) => void;
  labelUitleg: string;
}) {
  const [toon, setToon] = useState(false);
  const r = item.regeling;
  const uitlegId = `pasmaat-uitleg-${r.id}`;
  return (
    <li
      data-testid="strook-rij"
      style={{
        position: "relative",
        listStyle: "none",
        padding: "8px 0",
        borderBottom: laatste ? "none" : `1px solid ${color.lineFaint}`,
      }}
      onMouseEnter={() => setToon(true)}
      onMouseLeave={() => setToon(false)}
      onFocus={() => setToon(true)}
      onBlur={() => setToon(false)}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <button
          type="button"
          onClick={() => onOpen(item)}
          aria-describedby={uitlegId}
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "left",
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            fontSize: size.meta,
            fontWeight: weight.medium,
            color: color.ink,
            cursor: "pointer",
          }}
        >
          {r.naam}
          {r.verstrekker && r.verstrekker !== r.naam && (
            <span style={{ fontWeight: weight.regular, color: color.soft }}> · {r.verstrekker}</span>
          )}
        </button>
        <span
          style={{ fontSize: size.label, color: color.faint, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
          title="pasmaat 0-10: bedrag, eenvoud, tijd, passend (een rangorde, geen kans)"
        >
          pasmaat {komma(item.pasmaat.score)}
        </span>
        <StatusMerk item={item} />
      </div>
      <div style={{ fontSize: size.label, color: color.soft, marginTop: 2, lineHeight: 1.45 }}>
        {r.doelgroepEis ??
          `doelgroep-eis: ${legeVeldTekst(
            r.vlaggen,
            "doelgroep_eis",
            "doelgroepeis_niet_vermeld",
            "de officiële pagina noemt er geen",
          )}`}
      </div>
      {/* Funding fase 3+4 (G1 a): een niet-nagekeken vondst draagt ook hier zijn voorbehoud. */}
      {vondstLabel(r.vlaggen) && (
        <div data-nieuw-niet-nagekeken style={{ fontSize: size.label, color: color.accentAmber, marginTop: 2 }}>
          {vondstLabel(r.vlaggen)}
        </div>
      )}
      <div
        id={uitlegId}
        role="tooltip"
        hidden={!toon}
        style={{
          position: "absolute",
          zIndex: 5,
          left: 0,
          top: "100%",
          marginTop: 2,
          maxWidth: 320,
          background: color.ink,
          color: "#ffffff",
          borderRadius: radius.input,
          padding: "8px 10px",
          fontSize: size.label,
          lineHeight: 1.5,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ fontWeight: weight.medium, marginBottom: 2 }}>
          Waarom hier · pasmaat {komma(item.pasmaat.score)} van 10
        </div>
        {item.pasmaat.uitleg.map((u, i) => (
          <div key={i}>· {u}</div>
        ))}
        <div style={{ opacity: 0.75, marginTop: 4 }}>{labelUitleg}</div>
      </div>
    </li>
  );
}

/**
 * De strook. `sector` is een sleutel uit sectors.yaml of "sectorbreed";
 * `sectorLabel` is wat de lezer leest ("ICT"). Zonder `onOpen` opent een klik
 * de Funding-tab met de sector en de regelingnaam voorgezet.
 */
export function RegelingenStrook({
  sector,
  sectorLabel,
  vorm = null,
  bedrijfstype,
  limiet = 3,
  kop = "Regelingen die kunnen helpen",
  toelichting,
  onOpen,
}: {
  sector: string;
  sectorLabel: string;
  vorm?: string | null;
  bedrijfstype?: readonly string[];
  limiet?: number;
  kop?: string;
  toelichting?: ReactNode;
  onOpen?: (item: FundingVoorItem) => void;
}) {
  const res = useFundingVoor({ sector, vorm, bedrijfstype, limiet });
  const data = res.data;
  const label = data?.label ?? STROOK_LABEL;
  const labelUitleg = data?.labelUitleg ?? KANSRIJK_UITLEG;
  const naam = sector === SECTORBREED ? "sectorbreed" : sectorLabel;
  const filter = data?.afslag.filter ?? { sector: [sector], status: ["open", "aangekondigd"] };
  const open =
    onOpen ??
    ((item: FundingVoorItem) =>
      naarTab({ soort: "tab", tab: "funding", filter: { ...filter, zoek: item.regeling.naam } }));

  return (
    <div data-testid="regelingen-strook">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          flexWrap: "wrap",
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        <span>{kop}</span>
        <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: weight.regular }}>
          {naam}
          {vorm ? ` · ${vorm}` : ""}
        </span>
      </div>
      <div data-testid="strook-label" style={{ fontSize: size.label, color: color.faint, lineHeight: 1.5 }}>
        <Kansrijk label={label} uitleg={labelUitleg} />: gerangschikt op bedrag, eenvoud, tijd en
        passendheid{vorm ? ` bij de vorm ${vorm}` : ""}. Een regeling gaat over wie, wat en
        wanneer, nooit over een vaardigheid.
        {toelichting && <> {toelichting}</>}
      </div>

      {res.error ? (
        <div style={{ fontSize: size.label, color: color.faint, marginTop: 6 }}>
          De regelingen konden niet worden opgehaald ({res.error}).
        </div>
      ) : !data ? (
        <div style={{ fontSize: size.label, color: color.faint, marginTop: 6 }}>laden…</div>
      ) : data.items.length === 0 ? (
        <div data-testid="strook-leeg" style={{ fontSize: size.label, color: color.faint, marginTop: 6 }}>
          Voor {naam} is nu geen regeling waarvan de officiële pagina bevestigt dat hij open is.
          {data.waarschijnlijk && data.waarschijnlijk.aantal > 0 ? (
            <>
              {" "}
              {data.waarschijnlijk.aantal}{" "}
              {data.waarschijnlijk.aantal === 1 ? "regeling is" : "regelingen zijn"} waarschijnlijk
              aan te vragen (de pagina van het fonds beschrijft ze, zonder datum).{" "}
              <button
                type="button"
                data-testid="strook-waarschijnlijk"
                onClick={() =>
                  naarTab({ soort: "tab", tab: "funding", filter: data.waarschijnlijk!.filter })
                }
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: color.accentBlue,
                  cursor: "pointer",
                  fontSize: size.label,
                  textDecoration: "underline",
                }}
              >
                Bekijk ze op de Funding-tab
              </button>
            </>
          ) : (
            " Bekijk de tab Funding voor alle regelingen."
          )}
        </div>
      ) : (
        <>
          <ul style={{ margin: "4px 0 0", padding: 0 }}>
            {data.items.map((item, i) => (
              <Rij
                key={item.regeling.id}
                item={item}
                laatste={i === data.items.length - 1}
                onOpen={open}
                labelUitleg={labelUitleg}
              />
            ))}
          </ul>
          <button
            type="button"
            data-testid="strook-voet"
            onClick={() => naarTab({ soort: "tab", tab: "funding", filter })}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginTop: 4,
              font: "inherit",
              fontSize: size.label,
              color: color.accentBlue,
              cursor: "pointer",
            }}
          >
            alle {data.afslag.aantal} voor {naam} op de Funding-tab →
          </button>
        </>
      )}
    </div>
  );
}

/**
 * De strook in het trend-detail. De sector komt uit de trend zelf
 * (`trend.sectors`, V1-labels): één eenduidige sector → die sector, anders
 * alleen de sectorbrede regelingen, met een zin die dat zegt.
 */
export function TrendRegelingen({ sectors: ruw }: { sectors: readonly string[] | null | undefined }) {
  // TrendDetail opent ook vanuit chat, feed en verwijzing: nooit crashen op een ontbrekende lijst.
  const sectors: readonly string[] = Array.isArray(ruw) ? ruw : [];
  const { sleutel, eenduidig } = sectorVoorLabels(sectors);
  return (
    <div data-testid="trend-regelingen" style={{ marginBottom: 20 }}>
      <RegelingenStrook
        sector={sleutel}
        sectorLabel={eenduidig ? sectors[0]! : SECTORBREED}
        kop="Funding · regelingen die kunnen helpen"
        toelichting={
          eenduidig ? undefined : (
            <>
              Deze trend heeft geen eenduidige sector ({sectors.length ? sectors.join(", ") : "geen"});
              daarom alleen de sectorbrede regelingen.
            </>
          )
        }
      />
    </div>
  );
}
